import React, {useEffect, useMemo, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPen,
  faCirclePlay,
  faFolderOpen,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import {useResourcePersonCourses, useResourcePersonLessons} from "../store/resourcePerson/resourcePersonStore";
import ResourcePersonShell from "../components/resourcePerson/ResourcePersonShell";
import EmptyState from "../components/learner/EmptyState";
import RetryBanner from "../components/resourcePerson/RetryBanner";
import Toast from "../components/Toast";
import {htmlToText} from "../utils/toDisplayString";
import {IMAGE_HOST} from "../constants";

/**
 * Course preview for the resource person — answers "what will the learner
 * actually see?". Every module in order with its lessons rendered the way a
 * player renders them, so a broken video URL or a missing document is obvious
 * BEFORE submission rather than after. Read-only: nothing here mutates, so it
 * is safe to open for a course in any status.
 */

/**
 * Turn whatever the author pasted into something embeddable.
 *
 * VIDEO lessons are authored as a YouTube link, but the same field may hold an
 * uploaded file URL or a bare Cloudinary public id, so all three are normalised:
 *   youtube.com/watch?v=ID | youtu.be/ID | /shorts/ID  ->  /embed/ID
 *   vimeo.com/ID                                     ->  player.vimeo.com/video/ID
 *   relative public id ("fillop/lessons/x.pdf")     ->  IMAGE_HOST + id
 * Anything else is returned untouched (a direct .mp4, say) and rendered in an
 * iframe, which browsers play natively.
 */
export function toPreviewSrc(rawUrl) {
  const url = String(rawUrl || "").trim();
  if (!url) return "";

  const youtube = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`;

  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  // A bare Cloudinary public id is what the upload endpoints return; it is not
  // a valid URL on its own, so anchor it to the media host.
  if (!/^https?:\/\//i.test(url)) {
    return `${IMAGE_HOST || ""}${url}`;
  }
  return url;
}

/** Course metadata and the information learners see before opening lessons. */
const CourseShell = ({courseDetail, categoryLabel, categoryDescription, categoryImage, status, labelClass}) => {
  const cover =
    courseDetail.coverImage ||
    courseDetail.cover_image ||
    categoryImage?.image ||
    categoryImage?.imageUrl ||
    categoryImage?.image_url;
  const difficulty = courseDetail.difficultyLevel || courseDetail.difficulty_level;
  const price = courseDetail.price;
  return (
  <section className="rounded-xl border border-slate-200 bg-white p-4">
    {cover ? (
      <img
        src={toPreviewSrc(cover)}
        alt={`${courseDetail.title || "Course"} cover`}
        className="mb-4 aspect-[16/10] w-full max-w-[300px] rounded-lg object-cover object-top"
      />
    ) : null}
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2 className="m-0 text-sm font-bold text-slate-900">{courseDetail.title || "Course details"}</h2>
        <p className="m-0 mt-1 text-xs text-slate-500">
          {categoryLabel || courseDetail.categoryName || courseDetail.category_name || "Uncategorised"}
          {difficulty ? ` · ${difficulty}` : ""}
          {price !== undefined && price !== null && price !== "" ? ` · ${Number(price) === 0 ? "Free" : price}` : ""}
        </p>
      </div>
      {status ? (
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
          {status}
        </span>
      ) : null}
    </div>
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      {courseDetail.description ? (
        <div className="sm:col-span-2">
          <span className={labelClass}>Description</span>
          <p className="m-0 whitespace-pre-line text-sm text-slate-700">
            {htmlToText(courseDetail.description)}
          </p>
        </div>
      ) : null}
      {[
        ["Introduction", courseDetail.introduction],
        ["Learning outcomes", courseDetail.learningOutcomes ?? courseDetail.learning_outcomes],
        ["Requirements", courseDetail.requirements],
        ["Target audience", courseDetail.targetAudience ?? courseDetail.target_audience],
        ["Category details", categoryDescription],
      ]
        .filter(([, value]) => value)
        .map(([label, value]) => (
          <div key={label}>
            <span className={labelClass}>{label}</span>
            <p className="m-0 whitespace-pre-line text-sm text-slate-700">{htmlToText(value)}</p>
          </div>
        ))}
    </div>
  </section>
  );
};

/** Every module in order, each with its lessons, plus any ungrouped strays. */
const ModuleList = ({groups, moduleCount, totalLessons, renderLesson}) => (
  <section className="rounded-xl border border-slate-200 bg-white p-4">
    <h2 className="m-0 text-sm font-bold text-slate-900">
      Content ({moduleCount} module{moduleCount === 1 ? "" : "s"} · {totalLessons} lesson
      {totalLessons === 1 ? "" : "s"})
    </h2>

    {moduleCount === 0 ? (
      <div className="mt-3">
        <EmptyState
          icon={faFolderOpen}
          title="No modules yet"
          hint="Add a module in the builder; every lesson must be grouped under one."
        />
      </div>
    ) : (
      <div className="mt-3 grid gap-4">
        {groups.byModule.map(({module, items}, index) => (
          <div key={module.id} className="rounded-lg border border-slate-100">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-3 py-2">
              <h3 className="m-0 text-xs font-bold text-slate-900">
                {index + 1}. {module.title}
              </h3>
              <span className="text-[11px] text-slate-500">
                {items.length} lesson{items.length === 1 ? "" : "s"}
              </span>
            </div>
            {module.description ? (
              <p className="m-0 px-3 pt-2 text-[11px] text-slate-500">
                {htmlToText(module.description)}
              </p>
            ) : null}
            {items.length === 0 ? (
              <p className="m-0 px-3 py-2 text-xs text-slate-400">No lessons in this module yet.</p>
            ) : (
              <ol className="m-0 grid list-none gap-2 p-3">{items.map(renderLesson)}</ol>
            )}
          </div>
        ))}

        {groups.ungrouped.length > 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50/50">
            <div className="flex items-center gap-2 border-b border-amber-200 px-3 py-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-600" />
              <h3 className="m-0 text-xs font-bold text-amber-800">
                Ungrouped — no module ({groups.ungrouped.length})
              </h3>
            </div>
            <ol className="m-0 grid list-none gap-2 p-3">{groups.ungrouped.map(renderLesson)}</ol>
          </div>
        ) : null}
      </div>
    )}
  </section>
);

/** Read a lesson field that the API may return camelCase or snake_case. */
function lessonField(lesson, camel, snake) {
  return lesson?.[camel] ?? lesson?.[snake] ?? "";
}

// Types whose rich-text body is rendered below any media.
const mediaUrl = (item) =>
  item?.url || item?.fileUrl || item?.file_url || item?.publicId || item?.public_id || "";

const isImageUrl = (url, item) =>
  String(item?.mimeType || item?.mime_type || item?.format || "").toLowerCase().startsWith("image/") ||
  /\.(avif|gif|jpe?g|png|webp|svg)(?:[?#].*)?$/i.test(String(url || ""));

const ResourcePersonPreview = () => {
  const {id} = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();

  const {courseDetail, courseDetailLoading, courseDetailError, fetchCourseById} =
    useResourcePersonCourses();
  const {
    modulesLoading, modulesError, fetchModules,
    lessonsLoading, lessonsError, fetchLessons,
    fetchRpCategories,
    fetchLessonVideos,
    fetchLessonResources,
    fetchAssessments,
    fetchQuestions,
  } = useResourcePersonLessons();

  const [toast, setToast] = useState(null);
  const [categories, setCategories] = useState([]);
  const [previewModules, setPreviewModules] = useState([]);
  const [previewLessons, setPreviewLessons] = useState([]);
  const [lessonPreviewData, setLessonPreviewData] = useState({});
  const [previewDataLoading, setPreviewDataLoading] = useState(false);
  const [previewDataError, setPreviewDataError] = useState(null);
  const [previewReload, setPreviewReload] = useState(0);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    const loadPreview = async () => {
      setPreviewDataLoading(true);
      setPreviewDataError(null);
      setLessonPreviewData({});
      setCategories([]);
      setPreviewModules([]);
      setPreviewLessons([]);
      const [categoryResult, modulesResult, lessonsResult, courseResult] = await Promise.all([
        fetchRpCategories(),
        fetchModules(id),
        fetchLessons(id),
        fetchCourseById(id),
      ]);
      if (cancelled) return;
      if (categoryResult.success) setCategories(categoryResult.categories || []);
      if (modulesResult.success) setPreviewModules(modulesResult.modules || []);
      if (lessonsResult.success) setPreviewLessons(lessonsResult.lessons || []);
      const errors = [];
      if (!categoryResult.success) errors.push(`Categories: ${categoryResult.error}`);
      if (!modulesResult.success) errors.push(`Modules: ${modulesResult.error}`);
      if (!lessonsResult.success) errors.push(`Lessons: ${lessonsResult.error}`);
      if (!courseResult.success) errors.push(`Course: ${courseResult.error}`);
      if (!lessonsResult.success) {
        setPreviewDataLoading(false);
        setPreviewDataError(errors.join(" "));
        return;
      }

      const lessonEntries = await Promise.all((lessonsResult.lessons || []).map(async (lesson) => {
        const lessonId = lesson.id || lesson._id;
        const [videosResult, resourcesResult, assessmentsResult] = await Promise.all([
          fetchLessonVideos(id, lessonId),
          fetchLessonResources(id, lessonId),
          fetchAssessments(id, lessonId),
        ]);
        if (!videosResult.success) errors.push(`Video for "${lesson.title}": ${videosResult.error}`);
        if (!resourcesResult.success) errors.push(`File for "${lesson.title}": ${resourcesResult.error}`);
        if (!assessmentsResult.success) errors.push(`Assessments for "${lesson.title}": ${assessmentsResult.error}`);

        const assessments = assessmentsResult.assessments || [];
        const questionEntries = await Promise.all(assessments.map(async (assessment) => {
          if (String(assessment.kind || "").toUpperCase() !== "QUIZ") {
            return [String(assessment.id || assessment._id), []];
          }
          const assessmentId = assessment.id || assessment._id;
          if (!assessmentId) return ["", []];
          const questionResult = await fetchQuestions(id, lessonId, assessmentId);
          if (!questionResult.success) {
            errors.push(`Questions for "${assessment.title || "quiz"}": ${questionResult.error}`);
          }
          return [String(assessmentId), questionResult.questions || []];
        }));
        return [String(lessonId), {
          videos: videosResult.videos || [],
          resources: resourcesResult.resources || [],
          assessments,
          questions: Object.fromEntries(questionEntries.filter(([key]) => key)),
        }];
      }));

      if (cancelled) return;
      setLessonPreviewData(Object.fromEntries(lessonEntries));
      setPreviewDataLoading(false);
      setPreviewDataError(errors.length ? errors.join(" ") : null);
    };
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [
    id,
    isNew,
    fetchCourseById,
    fetchModules,
    fetchLessons,
    fetchRpCategories,
    fetchLessonVideos,
    fetchLessonResources,
    fetchAssessments,
    fetchQuestions,
    previewReload,
  ]);

  const labelClass = "mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500";
  const primaryBtn =
    "inline-flex items-center gap-2 rounded-md bg-sidebar-primary px-4 py-2 text-sm font-semibold text-sidebar-primary-foreground hover:bg-sidebar-accent";
  const ghostBtn =
    "inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";

  // Group lessons under their module. A lesson the server did not attach to any
  // module is surfaced in a trailing "Ungrouped" group instead of being dropped,
  // so the preview still accounts for every lesson on the course.
  const groups = useMemo(() => {
    const moduleIdOf = (lesson) => lesson?.moduleId ?? lesson?.module_id ?? "";
    const byModule = (previewModules || []).map((module) => ({
      module,
      items: (previewLessons || []).filter((l) =>
        String(moduleIdOf(l)) === String(module.id || module._id)
      ),
    }));
    const ungrouped = (previewLessons || []).filter(
      (l) => !(previewModules || []).some((m) =>
        String(m.id || m._id) === String(moduleIdOf(l))
      )
    );
    return {byModule, ungrouped};
  }, [previewModules, previewLessons]);

  const totalLessons = (previewLessons || []).length;

  /** An embedded frame. Used for both VIDEO and DOCUMENT sources. */
  const frame = (src, label) => (
    <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 bg-black">
      <iframe
        src={src}
        title={label}
        className="h-64 w-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );

  const courseCategoryIds = [
    ...(Array.isArray(courseDetail?.categoryIds) ? courseDetail.categoryIds : []),
    ...(Array.isArray(courseDetail?.category_ids) ? courseDetail.category_ids : []),
    courseDetail?.categoryId,
    courseDetail?.category_id,
    courseDetail?.category?.id,
    courseDetail?.category?._id,
  ].filter(Boolean).map(String);
  const courseCategories = [
    ...(Array.isArray(courseDetail?.categories) ? courseDetail.categories : []),
    ...(courseDetail?.category && typeof courseDetail.category === "object" ? [courseDetail.category] : []),
    ...categories.filter((category) =>
      courseCategoryIds.includes(String(category.id || category._id))
    ),
  ].filter((category, index, all) => {
    const categoryId = String(category.id || category._id || category.name || "");
    return categoryId && all.findIndex((item) =>
      String(item.id || item._id || item.name || "") === categoryId
    ) === index;
  });
  const categoryLabel = courseCategories.map((category) => category.name).filter(Boolean).join(", ");
  const categoryImage = courseCategories.find((category) => category.image || category.imageUrl || category.image_url);
  const categoryDescription = courseCategories.map((category) => category.description).filter(Boolean).join("\n\n");

  // ── One lesson, rendered as a learner would see it ────────────────────────
  const renderLesson = (lesson) => {
    const type = String(lesson.type || "").toUpperCase();
    const contentData = lessonPreviewData[String(lesson.id)] || {};
    const videos = contentData.videos?.length
      ? contentData.videos
      : lessonField(lesson, "videoUrl", "video_url")
        ? [{title: lesson.videoTitle || lesson.video_title || lesson.title, url: lessonField(lesson, "videoUrl", "video_url")}]
        : [];
    const resources = contentData.resources?.length
      ? contentData.resources
      : lessonField(lesson, "resourceUrl", "resource_url") || lessonField(lesson, "resourcePublicId", "resource_public_id")
        ? [{
            name: lessonField(lesson, "resourceName", "resource_name"),
            url: lessonField(lesson, "resourceUrl", "resource_url") ||
              lessonField(lesson, "resourcePublicId", "resource_public_id"),
          }]
        : [];
    const content = lesson?.content || "";
    const description = lessonField(lesson, "description", "lesson_description");
    const assessments = contentData.assessments || [];
    const contentType = videos.length
      ? "VIDEO"
      : resources.length
        ? "FILE"
        : content
          ? "TEXT"
          : type || "UNSET";

    return (
      <li key={lesson.id || lesson._id} className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="m-0 text-sm font-semibold text-slate-900">{lesson.title}</p>
            <p className="m-0 mt-0.5 text-xs text-slate-500">
              {contentType}
              {lesson.isRequired === false ? " · optional" : " · required"}
              {assessments.length
                ? ` · ${assessments.length} assessment${assessments.length === 1 ? "" : "s"}`
                : ""}
              {type === "QUIZ" && !assessments.length
                ? ` · pass ≥ ${lesson.passThreshold ?? lesson.pass_threshold ?? 70}%`
                : ""}
            </p>
          </div>
        </div>

        {description ? (
          <p className="m-0 mt-2 text-xs italic text-slate-600">{htmlToText(description)}</p>
        ) : null}

        {videos.map((video, index) => {
          const url = mediaUrl(video);
          return url ? (
            <div key={video.id || video._id || `video-${index}`} className="mt-3">
              <p className="m-0 text-xs font-semibold text-slate-700">{video.title || `Video ${index + 1}`}</p>
              {frame(toPreviewSrc(url), `${lesson.title} video`)}
              <a className="mt-1 inline-block break-all text-[11px] text-blue-700 underline"
                href={toPreviewSrc(url)} target="_blank" rel="noreferrer">
                Open video source
              </a>
            </div>
          ) : null;
        })}

        {resources.map((resource, index) => {
          const url = mediaUrl(resource);
          if (!url) return null;
          const src = toPreviewSrc(url);
          return (
            <div key={resource.id || resource._id || `resource-${index}`} className="mt-3">
              <p className="m-0 text-xs font-semibold text-slate-700">
                {resource.name || resource.title || `File ${index + 1}`}
              </p>
              {isImageUrl(url, resource) ? (
                <img
                  src={src}
                  alt={resource.name || resource.title || `${lesson.title} image`}
                  className="mt-2 max-h-96 w-full rounded-lg border border-slate-200 object-contain"
                />
              ) : /\.pdf(?:[?#].*)?$/i.test(url) ? (
                frame(src, `${lesson.title} file`)
              ) : (
                <a className="mt-2 inline-flex break-all text-sm text-blue-700 underline"
                  href={src} target="_blank" rel="noreferrer">
                  Open or download {resource.name || resource.title || "file"}
                </a>
              )}
            </div>
          );
        })}

        {content ? (
            <div
              className="mt-2 max-w-none text-sm text-slate-700"
              // Author-supplied rich text from the RP editor. No HTML sanitizer
              // is installed in this project, and the learner-facing player
              // injects the same markup — matching it here keeps the preview
              // faithful rather than adding a second, divergent sanitiser.
              dangerouslySetInnerHTML={{__html: content}}
            />
        ) : null}

        {assessments.map((assessment, assessmentIndex) => {
          const kind = String(assessment.kind || "ASSESSMENT").toUpperCase();
          const assessmentId = String(assessment.id || assessment._id || "");
          const questions = contentData.questions?.[assessmentId] || [];
          return (
            <section key={assessmentId || `${kind}-${assessmentIndex}`} className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="m-0 text-sm font-semibold text-slate-900">
                  {assessment.title || `${kind} ${assessmentIndex + 1}`}
                </h4>
                <span className="rounded-full bg-white px-2 py-1 text-[11px] font-semibold text-indigo-800">
                  {kind}
                </span>
              </div>
              {assessment.instructions ? (
                <p className="mb-0 mt-2 whitespace-pre-line text-sm text-slate-700">
                  {htmlToText(assessment.instructions)}
                </p>
              ) : null}
              <p className="mb-0 mt-2 text-[11px] text-slate-500">
                {assessment.isRequired === false || assessment.is_required === false ? "Optional" : "Required"}
                {kind === "QUIZ"
                  ? ` · Pass threshold ${assessment.passThreshold ?? assessment.pass_threshold ?? 70}%`
                  : ""}
              </p>
              {kind === "QUIZ" ? (
                questions.length ? (
                  <ol className="mb-0 mt-3 grid gap-2 pl-5">
                    {questions.map((question, questionIndex) => (
                      <li key={question.id || question._id || questionIndex} className="text-sm text-slate-800">
                        <p className="m-0 font-medium">{question.prompt}</p>
                        {Array.isArray(question.options) && question.options.length ? (
                          <ul className="mt-1 grid gap-1 pl-4 text-xs text-slate-600">
                            {question.options.map((option, optionIndex) => (
                              <li key={option.id || option._id || optionIndex}>
                                {option.text}{option.isCorrect || option.is_correct ? " · Correct answer" : ""}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {Array.isArray(question.acceptedAnswers || question.accepted_answers) &&
                        (question.acceptedAnswers || question.accepted_answers).length ? (
                          <p className="mb-0 mt-1 text-xs text-slate-600">
                            Accepted answers: {(question.acceptedAnswers || question.accepted_answers).join(", ")}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="mb-0 mt-2 text-xs text-slate-500">No quiz questions have been added.</p>
                )
              ) : null}
            </section>
          );
        })}
      </li>
    );
  };

  if (isNew) {
    return (
      <ResourcePersonShell title="Course preview" subtitle="Save a course first">
        <EmptyState
          icon={faFolderOpen}
          title="Nothing to preview"
          hint="Create a course draft first."
        />
      </ResourcePersonShell>
    );
  }

  const status = String(courseDetail?.status || "").toUpperCase();
  const loading = courseDetailLoading || modulesLoading || lessonsLoading || previewDataLoading;
  const loadError = courseDetailError || modulesError || lessonsError;
  const moduleCount = (previewModules || []).length;

  return (
    <ResourcePersonShell
      title="Course preview"
      subtitle={`${courseDetail?.title || "Course"} — what learners will see`}
      icon={faCirclePlay}
      actions={
        <>
          <button
            type="button"
            className={ghostBtn}
            onClick={() => navigate("/resource-person/content-library")}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Content library
          </button>
          <button
            type="button"
            className={primaryBtn}
            onClick={() => navigate(`/resource-person/content-library/course/${id}/build`)}
          >
            <FontAwesomeIcon icon={faPen} /> Edit course
          </button>
        </>
      }
    >
      {toast ? (
        <Toast message={toast.text} isSuccess={toast.ok} onClose={() => setToast(null)} />
      ) : null}

      {loadError || previewDataError ? (
        <RetryBanner
          message={[loadError, previewDataError].filter(Boolean).join(" ")}
          onRetry={() => setPreviewReload((current) => current + 1)}
        />
      ) : null}

      {loading ? (
        <p className="text-sm text-slate-500">Loading course…</p>
      ) : !courseDetail ? (
        <EmptyState
          icon={faFolderOpen}
          title="Course not found"
          hint="It may have been deleted, or the link is wrong."
        />
      ) : (
        <div className="grid gap-4">
          <CourseShell
            courseDetail={courseDetail}
            categoryLabel={categoryLabel}
            categoryDescription={categoryDescription}
            categoryImage={categoryImage}
            status={status}
            labelClass={labelClass}
          />
          <ModuleList
            groups={groups}
            moduleCount={moduleCount}
            totalLessons={totalLessons}
            renderLesson={renderLesson}
          />
        </div>
      )}
    </ResourcePersonShell>
  );
};


export default ResourcePersonPreview;
